import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Drawer,
  Group,
  MultiSelect,
  Stack,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconShieldCheck } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type { RoleDto } from './types';
import { useCreateRole, usePermissions, useUpdateRole } from './rolesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  role: RoleDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  permissionIds: string[];
}

export function RoleFormDrawer({ opened, onClose, role }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateRole();
  const update = useUpdateRole();
  const permsQuery = usePermissions();
  const submitting = create.isPending || update.isPending;
  const editing = role !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', permissionIds: [] },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: role?.code ?? '',
      nameAr: role?.nameAr ?? '',
      nameEn: role?.nameEn ?? '',
      permissionIds: (role?.permissionIds ?? []).map(String),
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, role]);

  // Group permission options by ModuleCode so the multi-select isn't a
  // 20-row flat list — much easier to scan and reason about which
  // permissions live where.
  const permissionOptions = useMemo(() => {
    const grouped = new Map<string, { value: string; label: string }[]>();
    (permsQuery.data ?? []).forEach((p) => {
      const list = grouped.get(p.moduleCode) ?? [];
      list.push({
        value: String(p.permissionId),
        label: i18n.language === 'ar' ? p.nameAr : (p.nameEn ?? p.nameAr),
      });
      grouped.set(p.moduleCode, list);
    });
    return Array.from(grouped.entries()).map(([group, items]) => ({ group, items }));
  }, [permsQuery.data, i18n.language]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      permissionIds: values.permissionIds.map((v) => Number(v)),
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: role.roleId, request: payload });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('roles.form.editTitle') : t('roles.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          {editing && role.isSystem && (
            <Alert
              variant="light"
              color="gray"
              icon={<IconShieldCheck size={16} stroke={1.8} />}
              title={t('roles.form.systemAlertTitle')}
            >
              {t('roles.form.systemAlertBody')}
            </Alert>
          )}

          <TextInput
            label={t('roles.form.codeLabel')}
            description={t('roles.form.codeHelp')}
            placeholder="MANAGER"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('roles.form.nameArLabel')}
            withAsterisk
            placeholder={t('roles.form.nameArPlaceholder')}
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('roles.form.nameEnLabel')}
            placeholder={t('roles.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <MultiSelect
            label={t('roles.form.permissionsLabel')}
            description={t('roles.form.permissionsHelp')}
            placeholder={t('roles.form.permissionsPlaceholder')}
            data={permissionOptions}
            value={form.values.permissionIds}
            onChange={(value) => form.setFieldValue('permissionIds', value)}
            searchable
            clearable
            disabled={permsQuery.isLoading}
            maxDropdownHeight={320}
          />

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
